import { Component, inject } from '@angular/core';
import { CommentsOrder } from '@banta/common';
import { CURRENT_ARTICLE_PROVIDER, CURRENT_PAGE_PROVIDER, ContentBlock, ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content';
import { ContentBlockViewComponent } from '@tytapp/content';

export interface CommentsBlock extends ContentBlock {
    identifier?: string;
    requirementType?: 'membership' | 'registration';
    defaultSortOrder?: CommentsOrder;
    shareTitle?: string;
    showHeader?: boolean;
    isPreview?: boolean;
}

@Component({
    template: `
    <mat-form-field appearance="outline" floatLabel="always">
        <mat-label>Topic</mat-label>
        <input name="identifier" matInput type="text" [(ngModel)]="block.identifier"
            placeholder="Automatic (page-specific)"
            [disabled]="locked" />
        <mat-hint>Comments blocks with the same Topic show the same comments.</mat-hint>
    </mat-form-field>

    <div class="field-row">
        <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Participation</mat-label>
            <mat-select [(ngModel)]="block.requirementType" placeholder="Requires Membership">
                <mat-option value="membership">Requires Membership</mat-option>
                <mat-option value="registration">Requires Registration</mat-option>
            </mat-select>
            <mat-hint>Leave blank for a comments section specific to this page</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Default Sort Mode</mat-label>
            <mat-select [(ngModel)]="block.defaultSortOrder" placeholder="Highest Likes First">
                <mat-option value="newest">Newest First</mat-option>
                <mat-option value="likes">Highest Likes First</mat-option>
                <mat-option value="oldest">Oldest First</mat-option>
            </mat-select>
            <mat-hint>Leave blank for a comments section specific to this page</mat-hint>
        </mat-form-field>
    </div>

    <mat-form-field appearance="outline" floatLabel="always">
        <mat-label>Share Title</mat-label>
        <input name="shareTitle" matInput type="text" [(ngModel)]="block.shareTitle"
            [placeholder]="page ? 'Page Title (' + page.title + ')' : 'Page Title'"
            [disabled]="locked" />
        <mat-hint>Title to use when sharing comments. Leave blank to use page title</mat-hint>
    </mat-form-field>

    <mat-checkbox name="showHeader" [(ngModel)]="block.showHeader">Show "Conversation" heading</mat-checkbox>
    `,
    styles: [
        `
        mat-form-field {
            width: 100%;
        }
        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<CommentsBlock> {
    private pageProvider = inject(CURRENT_PAGE_PROVIDER, { optional: true });

    get page() {
        return this.pageProvider?.page;
    }
}

@Component({
    template: `
        <tyt-comments
            [identifier]="identifier"
            [requirementType]="block.requirementType ?? 'membership'"
            [defaultSortOrder]="block.defaultSortOrder ?? 'likes'"
            [shareTitle]="shareTitle"
            [showHeader]="block.showHeader ?? true"
            [url]="url"
            ></tyt-comments>
        `
})
export class ViewComponent extends ContentBlockViewComponent<CommentsBlock> {
    private pageProvider = inject(CURRENT_PAGE_PROVIDER, { optional: true });

    get shareTitle() {
        if (this.block.shareTitle)
            return this.block.shareTitle;

        if (this.pageProvider?.page)
            return this.pageProvider.page.title;
    }
    get url() {
        if (this.pageProvider?.page) {
            return this.pageProvider.page.url ?? `/pages/${this.pageProvider.page.id}`;
        }

        return `https://tyt.com`;
    }

    get identifier() {
        if (this.block.identifier)
            return this.block.identifier;

        if (this.pageProvider?.page)
            return `page_${this.pageProvider?.page.id}`;

        return `global`;
    }
}

export const COMMENTS_BLOCK_TYPE: ContentBlockType<CommentsBlock> = {
    id: 'comments',
    label: 'Comments',
    icon: 'forum',
    preview: {
        isPreview: true
    },
    template: {
        requirementType: 'membership',
        defaultSortOrder: CommentsOrder.LIKES,
        showHeader: true
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent
};
